/**
 * Payout execution — `/v1/admin/payout-batches*`, `/v1/admin/payouts/*`.
 *
 * **The preview is a mutation, not a query.** It reads nothing the console caches: its answer is
 * a `snapshot_id` that the create step is checked against, and a cached preview would let an
 * operator create a batch from figures TanStack served from memory. Asking again is the point.
 *
 * **Every write carries an `Idempotency-Key` minted when its dialog opened.** These are the only
 * admin requests that move provider money, and a retried create without a key would build a
 * second batch for the same payables.
 *
 * **Nothing here re-derives eligibility, a threshold or a total.** The server assembles the run,
 * decides whether it needs a second pair of eyes, and refuses self-approval; this module asks and
 * shows what came back.
 *
 * **There is no hook for one payout on its own.** The batch detail already carries every payout.
 *
 * **A bank account is read per provider and vouched for with a reason.** The reason is the whole
 * value of the row: later the question is always *how* somebody checked — a cancelled cheque, a
 * call, a passbook — and "somebody clicked" answers none of it.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { idempotent } from '../idempotency';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type PayoutPreview = components['schemas']['PayoutPreviewResponse'];
export type PayoutPreviewGroup = components['schemas']['PayoutPreviewGroup'];
export type PayoutGroupBlocker = components['schemas']['PayoutGroupBlocker'];
export type PayoutBatchRow = components['schemas']['PayoutBatchRow'];
export type PayoutBatchDetail = components['schemas']['PayoutBatchDetail'];
export type PayoutRow = components['schemas']['PayoutRow'];
export type PayoutDetail = components['schemas']['PayoutDetail'];
export type PayoutLineRow = components['schemas']['PayoutLineRow'];
type BatchList = components['schemas']['PayoutBatchListResponse'];
export type BankAccountRow = components['schemas']['BankAccountRow'];
type BankAccountList = components['schemas']['BankAccountListResponse'];

export interface BatchQuery extends Record<string, unknown> {
  page: number;
  page_size: number;
  batch_status?: string;
}

/** Exported for its own test: an absent filter and an explicit one are different requests. */
export function batchSearch(params: BatchQuery): string {
  const search = new URLSearchParams({
    page: String(params.page),
    page_size: String(params.page_size),
  });
  if (params.batch_status !== undefined) search.set('batch_status', params.batch_status);
  return search.toString();
}

export function usePayoutBatches(params: BatchQuery) {
  return useQuery({
    queryKey: keys.payouts.batches(params),
    queryFn: () => authedClient.request<BatchList>(`/v1/admin/payout-batches?${batchSearch(params)}`),
  });
}

export function usePayoutBatch(batchId: number | null) {
  return useQuery({
    queryKey: keys.payouts.batch(batchId ?? 0),
    queryFn: () => authedClient.request<PayoutBatchDetail>(`/v1/admin/payout-batches/${batchId}`),
    enabled: batchId !== null,
  });
}

/**
 * Coarse invalidation on purpose. Approving a batch changes the batch, its payouts, the payable
 * board those payables came from and the queue of things waiting for a second pair of eyes; a
 * hand-maintained map of which keys each action touches goes stale the first time somebody adds
 * a filter.
 */
function usePayoutMutation<TArgs, TData>(fn: (args: TArgs) => Promise<TData>) {
  const client = useQueryClient();
  return useMutation<TData, unknown, TArgs>({
    mutationFn: fn,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.payouts.all() });
      void client.invalidateQueries({ queryKey: keys.payables.all() });
    },
  });
}

/** Never cached — see the module header. */
export function usePreviewPayoutBatch() {
  return useMutation<PayoutPreview, unknown, void>({
    mutationFn: () =>
      authedClient.request<PayoutPreview>('/v1/admin/payout-batches/preview', { method: 'POST' }),
  });
}

export function useCreatePayoutBatch() {
  return usePayoutMutation(
    ({ snapshotId, reason, key }: { snapshotId: string; reason: string; key: string }) =>
      authedClient.request<PayoutBatchDetail>('/v1/admin/payout-batches', {
        method: 'POST',
        body: { snapshot_id: snapshotId, reason },
        headers: idempotent(key),
      }),
  );
}

/** The server refuses the creator with `403 self_approval_forbidden`, whatever this screen offers. */
export function useDecidePayoutBatch() {
  return usePayoutMutation(
    ({
      batchId,
      decision,
      reason,
      key,
    }: {
      batchId: number;
      decision: 'approve' | 'reject';
      reason: string;
      key: string;
    }) =>
      authedClient.request<PayoutBatchDetail>(`/v1/admin/payout-batches/${batchId}/${decision}`, {
        method: 'POST',
        body: { reason },
        headers: idempotent(key),
      }),
  );
}

/**
 * Answers the run as it now stands, or refuses.
 *
 * With `payouts.rail` at `bank_manual` (the default) the server refuses with 409, and the
 * screen shows that refusal verbatim and points at mark-manual. On the Cashfree rail the
 * payouts are handed over and the run comes back `processing`.
 */
export function useExecutePayoutBatch() {
  return usePayoutMutation(({ batchId, reason, key }: { batchId: number; reason: string; key: string }) =>
    authedClient.request<PayoutBatchDetail>(`/v1/admin/payout-batches/${batchId}/execute`, {
      method: 'POST',
      body: { reason },
      headers: idempotent(key),
    }),
  );
}

export function useMarkPayoutManual() {
  return usePayoutMutation(
    ({
      payoutId,
      utr,
      processedOn,
      reason,
      key,
    }: {
      payoutId: number;
      utr: string;
      processedOn: string;
      reason: string;
      key: string;
    }) =>
      authedClient.request<PayoutDetail>(`/v1/admin/payouts/${payoutId}/mark-manual`, {
        method: 'POST',
        body: { utr, processed_on: processedOn, reason },
        headers: idempotent(key),
      }),
  );
}

/** Puts a failed payout back in play. It does not itself send anything — the server splits those. */
export function useRetryPayout() {
  return usePayoutMutation(({ payoutId, reason, key }: { payoutId: number; reason: string; key: string }) =>
    authedClient.request<PayoutDetail>(`/v1/admin/payouts/${payoutId}/retry`, {
      method: 'POST',
      body: { reason },
      headers: idempotent(key),
    }),
  );
}

export function useBankAccounts(panditProfileId: number) {
  return useQuery({
    queryKey: keys.payouts.bankAccounts(panditProfileId),
    queryFn: () =>
      authedClient.request<BankAccountList>(
        `/v1/admin/bank-accounts?pandit_profile_id=${panditProfileId}`,
      ),
  });
}

export function useVerifyBankAccount(panditProfileId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      authedClient.request<BankAccountRow>(`/v1/admin/bank-accounts/${id}/verify-manual`, {
        method: 'POST',
        body: { reason },
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: keys.payouts.bankAccounts(panditProfileId) }),
  });
}
